import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTeamStore } from '../../store/teamStore';
import { supabase } from '../../lib/supabase';
import {
  playerCardService,
  playerAttributeService,
  playerGoalService,
  playerDevelopmentNotesService,
  playerAttendanceService,
} from '../../services/playerCards';
import type { PlayerCard, PlayerAttribute, PlayerGoal, PlayerDevelopmentNote } from '../../types/playerCard';
import { ArrowLeft, Edit2, Target, Heart, Award, Calendar, TrendingUp, Trash2, Clock, BarChart3, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { AttributeEditor } from './AttributeEditor';
import { GoalEditor } from './GoalEditor';
import { AttendanceLog } from './AttendanceLog';
import { DevelopmentNotes } from './DevelopmentNotes';
import { getSuggestedPositions } from '../../utils/playerDesignations/index';
import { getStatKeysForSport } from '../../data/sportStats';
import { getCalculatedStats, formatCalculatedStat } from '../../utils/statCalculations';
import { formatHeight, formatWeight, getSpeedLabel } from '../../utils/formatting';
import { ConfirmModal } from '../ConfirmModal';
import { TennisStatsPanel } from './tennis/TennisStatsPanel';

type TabType = 'overview' | 'attributes' | 'goals' | 'attendance' | 'notes' | 'stats';

export const PlayerCardDetail: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const { currentTeam } = useTeamStore();
  const [card, setCard] = useState<PlayerCard | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [attributes, setAttributes] = useState<PlayerAttribute[]>([]);
  const [goals, setGoals] = useState<PlayerGoal[]>([]);
  const [notes, setNotes] = useState<PlayerDevelopmentNote[]>([]);
  const [attendanceStats, setAttendanceStats] = useState<any>(null);
  const [sportStats, setSportStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const loadCard = useCallback(async () => {
    if (!playerId || !currentTeam?.id) return;

    setLoading(true);
    try {
      // Get or create player card
      const playerCard = await playerCardService.getOrCreatePlayerCard(playerId, currentTeam.id);
      setCard(playerCard);

      // Load related data
      if (playerCard.id) {
        const [attrs, goalsData, notesData, stats, playerStats] = await Promise.all([
          playerAttributeService.getAttributes(playerCard.id, currentTeam.sport),
          playerGoalService.getGoals(playerCard.id),
          playerDevelopmentNotesService.getNotes(playerCard.id, 20),
          playerAttendanceService.getAttendanceStats(playerCard.player_id, currentTeam.id).catch(() => null),
          // Load sport stats
          supabase
            .from('sport_stats')
            .select('*')
            .eq('player_card_id', playerCard.id)
            .eq('team_id', currentTeam.id)
            .order('date', { ascending: false })
            .then(({ data, error }) => {
              if (error) {
                console.error('Error loading sport stats:', error);
                return [];
              }
              return data || [];
            }),
        ]);

        setAttributes(attrs);
        setGoals(goalsData);
        setNotes(notesData);
        setAttendanceStats(stats);
        setSportStats(playerStats);

        // Use saved designations from the card (no need to regenerate on every load)
        // Designations are updated when attributes are saved
      }
    } catch (error) {
      console.error('Error loading player card:', error);
      toast.error('Failed to load player card');
      // Ensure loading state is reset even on error
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [playerId, currentTeam?.id]);

  useEffect(() => {
    if (playerId && currentTeam?.id) {
      loadCard();
    }
  }, [playerId, currentTeam?.id, loadCard]);

  const handleUpdate = () => {
    loadCard();
  };

  const requestDelete = () => {
    setShowDeleteConfirm(true);
  };

  const performDelete = async () => {
    if (!card || !card.player_id) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('players')
        .delete()
        .eq('id', card.player_id);

      if (error) throw error;

      toast.success('Player deleted successfully');
      navigate('/players');
    } catch (error) {
      console.error('Error deleting player:', error);
      toast.error('Failed to delete player');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-600">Loading player card...</p>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Player card not found.</p>
        <Link to="/players" className="btn-primary">
          Back to Roster
        </Link>
      </div>
    );
  }

  // Attendance tracks practice/game check-in against a coach-run roster — not
  // meaningful for a personal (parent/player) account, which has no practices
  // to attend and no one marking them present.
  const tabs: Array<{ id: TabType; label: string }> = [
    { id: 'overview', label: 'Overview' },
    // Tennis gets a dedicated analytics tab (sc-339); other sports keep their
    // existing inline Overview-tab Statistics panel for now.
    ...(currentTeam?.sport === 'Tennis' ? [{ id: 'stats' as const, label: 'Stats' }] : []),
    { id: 'attributes', label: 'Attributes' },
    { id: 'goals', label: 'Goals' },
    ...(currentTeam?.isPersonal ? [] : [{ id: 'attendance' as const, label: 'Attendance' }]),
    { id: 'notes', label: 'Notes' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <button
            onClick={() => navigate('/players')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900 truncate">
              {card.nickname ? `"${card.nickname}" ` : ''}
              {card.player?.first_name} {card.player?.last_name}
            </h1>
            {card.player?.number && (
              <span className="text-gray-600 text-sm">#{card.player.number}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/player-cards/${playerId}/edit`}
            className="h-10 w-10 inline-flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
            title="Edit Card"
          >
            <Edit2 size={20} className="block" />
          </Link>
          <button
            onClick={requestDelete}
            className="h-10 w-10 inline-flex items-center justify-center hover:bg-red-50 rounded-full transition-colors text-red-600"
            title="Delete Player"
          >
            <Trash2 size={20} className="block" />
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={performDelete}
        title="Delete player"
        message={
          card?.player
            ? `Are you sure you want to delete ${card.player.first_name} ${card.player.last_name}? This will permanently remove the player and all associated data.`
            : 'This will permanently remove the player and all associated data.'
        }
        confirmLabel="Delete"
        confirmAriaLabel="delete"
        cancelLabel="Cancel"
        variant="danger"
        isConfirming={isDeleting}
      />

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 sm:px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                  ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* 2x2 grid: Row 1 = Positions | OVR (equal height), Row 2 = Experience | Throw/Bat/Hand/Shoot */}
              <div className="grid grid-cols-[1fr_140px] grid-rows-[auto_auto] gap-4 items-stretch">
                {/* Row 1 col 1 - Positions (same height as OVR via grid row) */}
                <div className="bg-gray-50 rounded-lg p-4 flex flex-col min-h-0">
                  <h3 className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide flex items-center gap-2">
                    <TrendingUp size={14} />
                    Positions
                  </h3>
                  {attributes.length > 0 && currentTeam?.sport ? (
                    <div className="space-y-2">
                      {(() => {
                        const suggested = getSuggestedPositions(
                          attributes,
                          currentTeam.sport
                        );
                        if (suggested.length === 0) {
                          return (
                            <p className="text-sm text-gray-400">
                              Add more attributes to see recommendations
                            </p>
                          );
                        }
                        const hasPosition = (group: string): boolean => {
                          return card.eligible_positions.includes(group);
                        };
                        return (
                          <div className="flex flex-wrap gap-1.5">
                            {suggested.map((group) => {
                              const alreadyHas = hasPosition(group);
                              return (
                                <span
                                  key={group}
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    alreadyHas
                                      ? 'bg-primary bg-opacity-20 text-primary border border-primary border-opacity-30'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}
                                >
                                  {group}
                                  {alreadyHas && ' ✓'}
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">
                      {attributes.length === 0
                        ? 'Add attributes to see recommendations'
                        : 'Add more attributes to see recommendations'}
                    </p>
                  )}
                </div>

                {/* Row 1 col 2 - OVR (same height as Positions via grid row) */}
                {card.ovr_rating !== undefined ? (
                  <div className="bg-gray-50 rounded-lg p-4 flex flex-col min-h-0">
                      <h3 className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide flex items-center gap-2">
                        <Award size={14} />
                        OVR
                      </h3>
                      <div className="flex items-baseline gap-1">
                        <div className="text-2xl font-bold text-primary">{card.ovr_rating}</div>
                        <div className="text-xs text-gray-500">/100</div>
                      </div>
                      {(card.offense_designation || card.defense_designation) && (
                        <div className="flex items-center gap-1 flex-wrap mt-2">
                          {card.offense_designation && (
                            <span className="text-xs text-green-600 font-semibold px-1.5 py-0.5 bg-green-50 rounded">
                              {card.offense_designation}
                            </span>
                          )}
                          {card.defense_designation && (
                            <span className="text-xs text-blue-600 font-semibold px-1.5 py-0.5 bg-blue-50 rounded">
                              {card.defense_designation}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                ) : (
                  <div className="min-h-[88px]" aria-hidden />
                )}

                {/* Row 2 col 1 - Experience */}
                <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col justify-center min-h-[60px]">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Experience</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {card.last_level_played ?? (card as { lastLevelPlayed?: string }).lastLevelPlayed ?? card.player?.experience ?? '—'}
                  </span>
                </div>

                {/* Row 2 col 2 - Throw/Bat | Shoot | Hand */}
                {(currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && (card.throwing_hand || card.batting_hand) ? (
                  <div className="flex flex-row gap-2 flex-1">
                    {card.throwing_hand && (
                        <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col flex-1 min-w-0 justify-center min-h-[60px]">
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Throw</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {card.throwing_hand === 'L' ? 'L' : 'R'}
                          </span>
                        </div>
                      )}
                      {card.batting_hand && (
                        <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col flex-1 min-w-0 justify-center min-h-[60px]">
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Bat</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {card.batting_hand === 'L' ? 'L' : card.batting_hand === 'R' ? 'R' : 'S'}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (currentTeam?.sport === 'Soccer' || currentTeam?.sport === 'Hockey') && card.shooting_preference ? (
                    <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col justify-center min-h-[60px]">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Shoot</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {card.shooting_preference === 'L' ? 'L' : 'R'}
                      </span>
                    </div>
                  ) : currentTeam?.sport && !['Baseball', 'Softball', 'Soccer', 'Hockey'].includes(currentTeam.sport) && card.handedness ? (
                    <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col justify-center min-h-[60px]">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Hand</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {card.handedness === 'L' ? 'L' : 'R'}
                      </span>
                    </div>
                  ) : (
                  <div className="min-h-[60px]" aria-hidden />
                )}
              </div>

              {/* Row 3: Health, plus No Shows | Late for coach accounts — attendance
                  tracking doesn't apply to a personal (parent/player) account. */}
              <div className={`grid gap-3 items-stretch ${currentTeam?.isPersonal ? 'grid-cols-1' : 'grid-cols-3'}`}>
                <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-2 min-h-[60px]">
                  <Heart size={14} className="text-gray-500 flex-shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Health</span>
                    <span
                      className={`text-sm font-semibold ${
                        card.health_status === 'healthy'
                          ? 'text-green-600'
                          : card.health_status === 'limited'
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}
                    >
                      {card.health_status === 'healthy'
                        ? 'Healthy'
                        : card.health_status === 'limited'
                        ? 'Limited'
                        : 'No Contact'}
                    </span>
                  </div>
                </div>
                {!currentTeam?.isPersonal && (
                  <>
                    <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-2 min-h-[60px]">
                      <Calendar size={14} className="text-gray-500 flex-shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">No Shows</span>
                        <span className="text-sm font-semibold text-red-600">
                          {attendanceStats?.practices_no_show || 0}
                        </span>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-2 min-h-[60px]">
                      <Clock size={14} className="text-gray-500 flex-shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Late</span>
                        <span className="text-sm font-semibold text-yellow-600">
                          {attendanceStats?.practices_late || 0}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Row 4: Height | Weight | Speed (sport-specific: 40 Time for football, Home to 1st for baseball/softball) */}
              <div className="grid grid-cols-3 gap-3 items-stretch">
                {(() => {
                  const player = card.player as { height?: string; weight?: number; speed?: number } | undefined;
                  const speedLabel = getSpeedLabel(currentTeam?.sport);
                  const speedValue = player?.speed != null ? (typeof player.speed === 'number' ? player.speed.toFixed(2) : String(player.speed)) : null;
                  return (
                    <>
                      <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col justify-center min-h-[60px]">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Height</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {player?.height ? formatHeight(player.height) : '—'}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col justify-center min-h-[60px]">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Weight</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {player?.weight != null ? formatWeight(player.weight) : '—'}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg px-3 py-2 flex flex-col justify-center min-h-[60px]">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">{speedLabel}</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {speedValue ?? '—'}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Statistics Section — Tennis has its own dedicated, properly-labeled Stats tab instead (sc-339);
                  this generic panel falls back to raw stat_key names it was never taught to translate. */}
              {currentTeam?.sport !== 'Tennis' && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide flex items-center gap-2">
                  <BarChart3 size={14} />
                  Statistics
                </h3>
                {sportStats.length > 0 ? (
                  <div className="space-y-4">
                    {(() => {
                      const statsByKey: Record<string, { total: number; count: number; latest?: any }> = {};
                      sportStats.forEach(stat => {
                        if (!statsByKey[stat.stat_key]) {
                          statsByKey[stat.stat_key] = { total: 0, count: 0 };
                        }
                        statsByKey[stat.stat_key].total += parseFloat(stat.stat_value);
                        statsByKey[stat.stat_key].count += 1;
                        if (!statsByKey[stat.stat_key].latest || new Date(stat.date) > new Date(statsByKey[stat.stat_key].latest.date)) {
                          statsByKey[stat.stat_key].latest = stat;
                        }
                      });

                      const gameKeys = new Set(
                        sportStats.map(s => (s.game_id ? `${s.date}_${s.game_id}` : `${s.date}_${s.opponent ?? 'x'}`))
                      );
                      const gamesPlayed = gameKeys.size;

                      const statKeys = currentTeam?.sport ? getStatKeysForSport(currentTeam.sport) : [];
                      const getFullLabel = (key: string) => {
                        const stat = statKeys.find(s => s.key === key);
                        return stat ? stat.label : key;
                      };
                      const shortLabel = (key: string) => {
                        const map: Record<string, string> = {
                          H_Allowed: 'HA', R_Allowed: 'RA', BB_Allowed: 'BBA', HBP_Allowed: 'HBPA', Pitches: 'P',
                        };
                        return map[key] ?? key;
                      };

                      const rawStatsArray = Object.entries(statsByKey).map(([key, data]) => ({
                        stat_key: key,
                        stat_value: data.total,
                      }));
                      const calculatedStats = currentTeam?.sport
                        ? getCalculatedStats(currentTeam.sport, rawStatsArray, currentTeam.innings)
                        : {};

                      const rawStatEntries = Object.entries(statsByKey);
                      const calculatedStatEntries = Object.entries(calculatedStats);

                      const isBaseballOrSoftball = currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball';
                      const BATTING_CALC = ['AVG', 'OBP', 'SLG', 'OPS'];
                      const PITCHING_CALC = ['ERA', 'WHIP'];
                      const PITCHING_RAW = new Set(['IP', 'W', 'L', 'SV', 'ER', 'ERA', 'K', 'H_Allowed', 'R_Allowed', 'BB_Allowed', 'BF', 'Pitches', 'HBP_Allowed', 'WP', 'Balk', 'Balls', 'Strikes']);
                      // Display order for stat tiles (priority stats first; others follow)
                      const BATTING_RAW_ORDER = ['AB', 'H', 'SO', 'RBI', 'BB', 'R', '1B', '2B', '3B', 'HR', 'HBP', 'SB', 'SF', 'FC', 'E', 'FO'];
                      const PITCHING_RAW_ORDER = ['K', 'IP', 'ER', 'Pitches', 'BF', 'BB_Allowed', 'H_Allowed', 'R_Allowed', 'HBP_Allowed', 'Balk', 'WP', 'W', 'L', 'SV', 'Balls', 'Strikes'];
                      const sortByOrder = <T extends [string, unknown]>(entries: T[], order: string[]): T[] =>
                        [...entries].sort((a, b) => {
                          const i = order.indexOf(a[0]);
                          const j = order.indexOf(b[0]);
                          const iVal = i === -1 ? order.length : i;
                          const jVal = j === -1 ? order.length : j;
                          return iVal - jVal;
                        });

                      const battingCalcEntries = isBaseballOrSoftball ? sortByOrder(calculatedStatEntries.filter(([k]) => BATTING_CALC.includes(k)), BATTING_CALC) : calculatedStatEntries;
                      const pitchingCalcEntries = isBaseballOrSoftball ? sortByOrder(calculatedStatEntries.filter(([k]) => PITCHING_CALC.includes(k)), PITCHING_CALC) : [];
                      const battingRawEntries = isBaseballOrSoftball ? sortByOrder(rawStatEntries.filter(([k]) => !PITCHING_RAW.has(k)), BATTING_RAW_ORDER) : rawStatEntries;
                      const pitchingRawEntries = isBaseballOrSoftball ? sortByOrder(rawStatEntries.filter(([k]) => PITCHING_RAW.has(k) && k !== 'ERA'), PITCHING_RAW_ORDER) : [];

                      const hasBatting = battingCalcEntries.length > 0 || battingRawEntries.length > 0;
                      const hasPitching = pitchingCalcEntries.length > 0 || pitchingRawEntries.length > 0;

                      const CALC_FULL: Record<string, string> = { AVG: 'Batting average', OBP: 'On-base %', SLG: 'Slugging %', OPS: 'On-base + slugging', ERA: 'Earned Run Average', WHIP: 'Walks + hits per IP' };
                      const legendMap = new Map<string, string>();
                      const addLegend = (abbr: string, full: string) => { if (abbr && full && !legendMap.has(abbr)) legendMap.set(abbr, full); };
                      if (isBaseballOrSoftball) {
                        battingCalcEntries.forEach(([k]) => addLegend(k, CALC_FULL[k] ?? getFullLabel(k)));
                        battingRawEntries.forEach(([k]) => addLegend(shortLabel(k), getFullLabel(k)));
                        pitchingCalcEntries.forEach(([k]) => addLegend(k, CALC_FULL[k] ?? getFullLabel(k)));
                        pitchingRawEntries.forEach(([k]) => addLegend(shortLabel(k), getFullLabel(k)));
                      } else {
                        calculatedStatEntries.forEach(([k]) => addLegend(k, CALC_FULL[k] ?? k));
                        rawStatEntries.forEach(([k]) => addLegend(shortLabel(k), getFullLabel(k)));
                      }
                      const stripParens = (s: string) => s.replace(/\s*\([^)]*\)/g, '').trim();
                      const uniqueLegend = Array.from(legendMap.entries()).map(([abbr, full]) => ({ abbr, full: stripParens(full) }));

                      const StatTile = ({ statKey, abbr, value }: { statKey: string; abbr: string; value: number }) => (
                        <div key={statKey} className="bg-white rounded p-3 border border-gray-200">
                          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 whitespace-nowrap">{abbr}</div>
                          <div className="text-lg font-bold text-gray-900">
                            {value % 1 === 0 ? value : value.toFixed(2)}
                          </div>
                        </div>
                      );

                      const CalcTile = ({ statKey, value }: { statKey: string; value: number }) => (
                        <div key={statKey} className="bg-blue-50 rounded p-3 border border-blue-200">
                          <div className="text-xs text-gray-600 uppercase tracking-wide mb-1 whitespace-nowrap">{statKey}</div>
                          <div className="text-lg font-bold text-blue-900">{formatCalculatedStat(value, statKey)}</div>
                        </div>
                      );

                      return (
                        <>
                          <div className="flex items-center gap-2 mb-4">
                            <div className="bg-white rounded px-3 py-2 border border-gray-200">
                              <span className="text-xs text-gray-500 uppercase tracking-wide whitespace-nowrap">Games played</span>
                              <span className="ml-2 text-lg font-bold text-gray-900">{gamesPlayed}</span>
                            </div>
                          </div>
                          {isBaseballOrSoftball && hasBatting && (
                            <div className="mb-4 rounded-lg border-2 border-green-200 bg-green-50/80 p-4">
                              <h4 className="text-sm font-semibold text-green-800 mb-3 uppercase tracking-wide text-center">Batting</h4>
                              <div className="grid grid-cols-3 gap-3">
                                {battingCalcEntries.map(([key, value]) => (
                                  <CalcTile key={key} statKey={key} value={value} />
                                ))}
                                {battingRawEntries.map(([key, data]) => (
                                  <StatTile key={key} statKey={key} abbr={shortLabel(key)} value={data.total} />
                                ))}
                              </div>
                            </div>
                          )}
                          {isBaseballOrSoftball && hasPitching && (
                            <div className="mb-4 rounded-lg border-2 border-blue-200 bg-blue-50/80 p-4">
                              <h4 className="text-sm font-semibold text-blue-800 mb-3 uppercase tracking-wide text-center">Pitching</h4>
                              <div className="grid grid-cols-3 gap-3">
                                {pitchingCalcEntries.map(([key, value]) => (
                                  <CalcTile key={key} statKey={key} value={value} />
                                ))}
                                {pitchingRawEntries.map(([key, data]) => (
                                  <StatTile key={key} statKey={key} abbr={shortLabel(key)} value={data.total} />
                                ))}
                              </div>
                            </div>
                          )}
                          {(!isBaseballOrSoftball || (!hasBatting && !hasPitching)) && (
                            <>
                              {calculatedStatEntries.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Calculated</h4>
                                  <div className="grid grid-cols-3 gap-3">
                                    {calculatedStatEntries.map(([key, value]) => (
                                      <CalcTile key={key} statKey={key} value={value} />
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div>
                                <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Totals</h4>
                                <div className="grid grid-cols-3 gap-3 mt-2">
                                  {rawStatEntries.map(([key, data]) => (
                                    <StatTile key={key} statKey={key} abbr={shortLabel(key)} value={data.total} />
                                  ))}
                                </div>
                              </div>
                            </>
                          )}
                          {uniqueLegend.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-200">
                              <p className="text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Legend</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                                {uniqueLegend.map((e) => (
                                  <div key={e.abbr} className="leading-tight">
                                    <span className="font-medium text-gray-700">{e.abbr}</span> = {e.full}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No statistics recorded yet</p>
                )}
              </div>
              )}

              {/* Current Active Goal */}
              {goals.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide flex items-center gap-2">
                    <Target size={14} />
                    Current Active Goal
                  </h3>
                  {(() => {
                    const activeGoal = goals.find(g => g.status === 'in_progress' || g.status === 'not_started');
                    if (activeGoal) {
                      return (
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-gray-900 line-clamp-2">
                            {activeGoal.goal_text}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              activeGoal.status === 'in_progress'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {activeGoal.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                            </span>
                            {activeGoal.target_date && (
                              <span className="text-xs text-gray-500">
                                Due: {new Date(activeGoal.target_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <p className="text-sm text-gray-400">No active goals</p>
                    );
                  })()}
                </div>
              )}

              {/* Health Notes - Only show if exists */}
              {card.health_notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide flex items-center gap-2">
                    <Heart size={14} />
                    Health Notes
                  </h3>
                  <p className="text-sm text-gray-700">{card.health_notes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'stats' && (
            <TennisStatsPanel sportStats={sportStats} teamId={card.team_id} />
          )}

          {activeTab === 'attributes' && (
            <AttributeEditor
              cardId={card.id}
              sport={currentTeam?.sport || 'Football'}
              attributes={attributes}
              onUpdate={handleUpdate}
            />
          )}

          {activeTab === 'goals' && (
            <GoalEditor
              cardId={card.id}
              goals={goals}
              onUpdate={handleUpdate}
            />
          )}

          {activeTab === 'attendance' && (
            <AttendanceLog
              playerId={card.player_id}
              teamId={card.team_id}
            />
          )}

          {activeTab === 'notes' && (
            <DevelopmentNotes
              cardId={card.id}
              notes={notes}
              onUpdate={handleUpdate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

